// utils/clientDomain.js - Utilidades para detectar el cliente basado en el subdominio

/**
 * Extrae el subdominio del hostname actual
 * @returns {string|null} - El subdominio o null si no existe
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Dominios base para producción y desarrollo.
  // 'herrerahavas.com.ec' ANTES que 'herrerahavas.com': el primero contiene al
  // segundo como substring y el match es por includes() (dominio de contingencia
  // por el secuestro del DNS de herrerahavas.com).
  const baseDomains = ['herrerahavas.com.ec', 'herrerahavas.com', 'herrerahavas.local'];
  
  // Buscar el dominio base que coincida
  const matchedDomain = baseDomains.find(domain => hostname.includes(domain));
  
  if (!matchedDomain) {
    return null; // No es un dominio de Herrera Havas
  }
  
  // Extraer el subdominio
  const parts = hostname.replace(matchedDomain, '').split('.');
  const subdomain = parts.filter(p => p.length > 0).join('.');
  
  return subdomain || null;
};

/**
 * Verifica si estamos en un subdominio de cliente
 * @returns {boolean}
 */
export const isClientDomain = () => {
  const subdomain = getSubdomain();
  
  // Si no hay subdominio, o es 'www', 'galaxy', 'api', no es cliente
  if (!subdomain) return false;
  
  const internalSubdomains = ['www', 'galaxy', 'api', 'galaxy-api'];
  return !internalSubdomains.includes(subdomain);
};

/**
 * Obtiene la configuración del cliente basada en el subdominio
 * @returns {Object|null} - Configuración del cliente o null
 */
export const getClientConfig = () => {
  const subdomain = getSubdomain();
  
  if (!subdomain || !isClientDomain()) {
    return {
      isClientDomain: false,
      subdomain: null,
      clientName: 'Galaxy Metrics',
      logoUrl: null,
      logoType: 'falcon',
      brandConfig: null
    };
  }
  
  // Mapeo de subdominios a configuración de clientes
  const clientConfigs = {
    ford: {
      clientName: 'Ford Ecuador',
      logoUrl: '/assets/img/logos/ford-logo.png',
      logoType: 'ford',
      brandConfig: {
        primaryColor: '#003478',
        secondaryColor: '#FF6F00',
        accentColor: '#003478'
      }
    },
    utpl: {
      clientName: 'UTPL',
      logoUrl: '/assets/img/logos/utpl-logo.png',
      logoType: 'utpl',
      brandConfig: {
        primaryColor: '#003876',
        secondaryColor: '#FFD700',
        accentColor: '#003876'
      }
    },
    trivo: {
      clientName: 'Trivo',
      logoUrl: '/assets/img/logos/trivo-logo.png',
      logoType: 'trivo',
      brandConfig: {
        primaryColor: '#E31E24',
        secondaryColor: '#000000',
        accentColor: '#E31E24'
      }
    },
    danec: {
      clientName: 'Danec',
      logoUrl: '/assets/img/logos/danec-logo.png',
      logoType: 'danec',
      brandConfig: {
        primaryColor: '#003F87',
        secondaryColor: '#00A3E0',
        accentColor: '#003F87'
      }
    },
    claro: {
      clientName: 'Claro Ecuador',
      logoUrl: '/assets/img/logos/claro-logo.png',
      logoType: 'falcon', // Usar logo por defecto temporalmente
      brandConfig: {
        primaryColor: '#E50000',
        secondaryColor: '#333333',
        accentColor: '#E50000'
      }
    },
    // Agregar más clientes aquí...
  };
  
  const config = clientConfigs[subdomain] || {
    clientName: subdomain.toUpperCase(),
    logoUrl: null,
    logoType: 'falcon',
    brandConfig: null
  };
  
  return {
    isClientDomain: true,
    subdomain,
    defaultRoute: '/dashboard/custom',
    ...config
  };
};

/**
 * Determina la ruta de redirección después del login
 * basada en si es un cliente o usuario interno
 * @param {Object} user - Usuario autenticado
 * @returns {string} - Ruta a la que redirigir
 */
export const getLoginRedirectPath = (user) => {
  const clientConfig = getClientConfig();
  
  // Si estamos en un subdominio de cliente
  if (clientConfig && clientConfig.isClientDomain) {
    // Verificar si el usuario tiene el rol de cliente externo
    const hasClientRole = user?.roles?.some(role => 
      role.name === 'cliente_externo' || 
      role.name === 'client' || 
      role.name === 'CLIENT'
    );
    
    if (hasClientRole) {
      return '/dashboard/custom';
    }
  }
  
  // Para usuarios internos o admins, ir al dashboard normal
  return '/dashboard';
};

export default {
  getSubdomain,
  isClientDomain,
  getClientConfig,
  getLoginRedirectPath,
};

